/**
 * @fileoverview Syndicator Showdown — public profile hydration
 * @module services/publicProfiles
 *
 * Batch-fetches player_profiles/{uid} docs (public read, safe fields only:
 * username, displayName, photoURL, foundingMember, elo*) for a list of
 * user ids. Used to hydrate public identity (stable name + avatar) onto
 * leaderboard rows and other player-facing lists, since those rows may
 * only carry a denormalised displayName snapshot and callers cannot read
 * another user's private users/{uid} doc.
 */

import { collection, doc, documentId, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';

const IN_QUERY_CHUNK_SIZE = 30; // Firestore 'in' operator limit

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * Fetches player_profiles/{uid} for every uid in `uids`, returning a map
 * of uid -> profile data (or null if no public profile doc exists yet).
 *
 * @param {string[]} uids
 * @returns {Promise<Object<string, Object|null>>}
 */
export async function hydratePublicProfiles(uids) {
  const unique = [...new Set(uids.filter(Boolean))];
  const map = {};
  if (unique.length === 0) return map;

  unique.forEach(uid => { map[uid] = null; });

  await Promise.all(chunk(unique, IN_QUERY_CHUNK_SIZE).map(async batch => {
    try {
      const snap = await getDocs(query(
        collection(db, 'player_profiles'),
        where(documentId(), 'in', batch)
      ));
      snap.docs.forEach(d => { map[d.id] = d.data(); });
    } catch {
      // Fall back to individual reads if the 'in' query fails for any
      // reason (e.g. emulator quirks) — never let hydration block rendering.
      await Promise.all(batch.map(async uid => {
        try {
          const single = await getDoc(doc(db, 'player_profiles', uid));
          map[uid] = single.exists() ? single.data() : null;
        } catch {
          map[uid] = null;
        }
      }));
    }
  }));

  return map;
}
